// draft-merge-request-push.ts
//
// P7 §7/D01: push candidate + tạo Draft MR CÙNG một lệnh, bằng GitLab push
// options (KHÔNG có `glab` — D02). Argv CỐ ĐỊNH theo decision D01:
//   git push -o merge_request.create -o merge_request.draft
//             -o merge_request.target=<target> <remote> HEAD:refs/heads/<branch>
//
// Policy check (evaluatePolicy, operation "git_push") + operation_intent
// journal TRƯỚC push — cùng khuôn với commit-owner.ts's intent-before-
// side-effect (§3.4): crash giữa intent và push cần quan sát thực tại (MR
// evidence client) trước khi coi là "chưa push", không blind retry.
import { randomUUID } from "node:crypto";
import { buildCandidateCommitWithoutControllerPaths } from "./candidate-commit-without-controller-paths.js";
import type { GitRunner } from "./commit-owner-git-ops.js";
import { decisionLedgerMergeRequestLines } from "./decision-ledger-merge-request-block.js";
import { readDecisionLedger } from "./decision-ledger-projection.js";
import { buildDraftMergeRequestPushArgv } from "./draft-merge-request-push-argv.js";
import type { MissionStore } from "./mission-store.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";
import { redactEvidence } from "./retry-policy-evidence.js";
import { collectDoneTicketReportedGaps } from "./ticket-reported-gaps.js";

// Argv builder split out (03b, 200-line budget); re-exported for existing importers.
export { buildDraftMergeRequestPushArgv } from "./draft-merge-request-push-argv.js";

export interface PushDraftMergeRequestInput {
  store: MissionStore;
  repoDir: string;
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  remote: string;
  branch: string;
  target: string;
  candidateSha: string;
  baseSha: string;
  policyDigest: string;
  reviewStateRoot: string;
  missionId: string;
  /**
   * N-04 seam: how the pushed commit is derived from `candidateSha`. Defaults
   * to candidate-commit-without-controller-paths.ts (temporary-index plumbing,
   * parent = `baseSha`, which production reads from `git rev-parse
   * <target_branch>`). Injectable for tests whose `baseSha` is a fixture value
   * rather than a commit the local repo holds.
   */
  buildCandidate?: typeof buildCandidateCommitWithoutControllerPaths;
  /** D16: deploy recipe id for the MR's `Autopilot-Deploy:` trailer; `null` = none configured. */
  deployRecipeId?: string | null;
  /** Round 3 M-A: see buildDraftMergeRequestPushArgv — set only when the journal records a push to this branch. */
  forceWithLeaseSha?: string;
  /**
   * V2-1 B6 (I4a): known secret values the MR description redactor masks on
   * top of the generic patterns. Default `[]` = patterns only; phase 04
   * (secrets mount) supplies the mission's values.
   */
  secretValues?: string[];
}

export type PushDraftMergeRequestResult =
  /** `pushedSha`: the commit the MR head now points at (X2 binds remote evidence to it). */
  | { status: "pushed"; stdout: string; pushedSha: string }
  | { status: "policy_denied"; reason: string }
  | { status: "push_failed"; stderr: string };

/**
 * Push candidate as a draft MR. Policy re-checked here (review receipt +
 * risk gate live inside evaluatePolicy for operation "git_push" — see
 * policy-adapter.ts §3), operation_intent journaled BEFORE the actual git
 * push so a crash mid-push is reconcilable via MR evidence, never blindly
 * retried (git push -o merge_request.create is NOT safely re-runnable —
 * re-running after a successful push errors or, worse, edits the existing
 * MR's options unexpectedly).
 */
export async function pushDraftMergeRequest(
  input: PushDraftMergeRequestInput,
): Promise<PushDraftMergeRequestResult> {
  // N-04: derive the commit to push BEFORE the policy check so the command the
  // policy audits is the command that runs. Building it is local plumbing on a
  // temporary index (a dangling commit object at most, gc-able, no ref, no
  // worktree change) — the only side effect that matters, the push, still
  // happens strictly after `allow`.
  const buildCandidate = input.buildCandidate ?? buildCandidateCommitWithoutControllerPaths;
  // A2: the candidate message is the draft MR description — carry what the
  // done tickets' workers declared unverified (projection, redacted).
  const reportedGaps = collectDoneTicketReportedGaps(input.store.state);
  const secretValues = input.secretValues ?? [];
  let pushedSha: string;
  try {
    // V2-1 E11: "Quyết định trong mission" only when the journal ledger has
    // ≥ 1 entry (empty ⇒ baseline description); the WHOLE description is
    // redacted before the commit carrying it is built (B6).
    const decisionLines = decisionLedgerMergeRequestLines(
      await readDecisionLedger(input.store.controlRoot),
    );
    ({ pushedSha } = await buildCandidate({
      gitRunner: input.gitRunner,
      repoDir: input.repoDir,
      candidateSha: input.candidateSha,
      baseSha: input.baseSha,
      missionId: input.missionId,
      uncoveredAreas: reportedGaps.uncoveredAreas,
      remainingWork: reportedGaps.remainingWork,
      uncoveredDeclaredNone: reportedGaps.declaredNone,
      uncoveredNotDeclared: reportedGaps.notDeclared,
      deployRecipeId: input.deployRecipeId,
      decisionLines,
      redactDescription: (message) => redactEvidence(message, secretValues),
    }));
  } catch (err) {
    // Code review 260915 M1: a candidate that cannot be derived (base not an
    // ancestor of the mission HEAD, or plumbing failure) is a push that never
    // started — no policy audit of a bogus command, no intent, no push. The
    // release flow already maps `push_failed` to a reason; a thrown error
    // here would instead take the whole release step down.
    const message = err instanceof Error ? err.message : String(err);
    return { status: "push_failed", stderr: message };
  }
  const argv = buildDraftMergeRequestPushArgv({ ...input, source: pushedSha });

  const verdict = await input.evaluatePolicy({
    actor: "supervisor",
    operation: "git_push",
    repo: input.repoDir,
    candidateSha: input.candidateSha,
    baseSha: input.baseSha,
    policyDigest: input.policyDigest,
    reviewStateRoot: input.reviewStateRoot,
    missionId: input.missionId,
    command: `git ${argv.join(" ")}`,
  });
  if (verdict.decision !== "allow") {
    return { status: "policy_denied", reason: verdict.reason };
  }

  const operationId = randomUUID();
  await input.store.commit([
    {
      event_id: `${operationId}-intent`,
      operation_id: operationId,
      delta: {
        kind: "operation_intent",
        operation_id: operationId,
        operation_kind: "git_push",
        params: {
          remote: input.remote,
          branch: input.branch,
          target: input.target,
          // `candidate_sha` stays the mission HEAD — the key review-staging,
          // G3 and deploy receipts are bound to. `pushed_sha` is what the
          // remote branch actually points at (N-04).
          candidate_sha: input.candidateSha,
          pushed_sha: pushedSha,
          // The base the pushed commit was built on, and when the push was
          // recorded: a re-run while the MR pipeline runs reuses this push and
          // binds to them instead of the (possibly moved) local target branch
          // (recorded-draft-mr-push.ts).
          base_sha: input.baseSha,
          recorded_at: new Date().toISOString(),
          // The recipe the MR trailer was written under (null = none): a reuse
          // under a different one warns on the G3 line (release-flow-candidate-push.ts).
          deploy_recipe_id: input.deployRecipeId ?? null,
        },
      },
    },
  ]);

  const res = await input.gitRunner(argv, input.repoDir);

  await input.store.commit([
    {
      event_id: `${operationId}-result`,
      operation_id: operationId,
      delta: {
        kind: "operation_result",
        operation_id: operationId,
        outcome: res.status === 0 ? "pushed" : "push_failed",
        // The id the remote now holds — the pushed candidate, not the mission HEAD.
        remote_id: pushedSha,
      },
    },
  ]);

  if (res.status !== 0) {
    return { status: "push_failed", stderr: res.stderr };
  }
  return { status: "pushed", stdout: res.stdout, pushedSha };
}
